import React, { useState, useEffect, useMemo } from "react";
import { CheckCircle, BarChart3, Edit3, Instagram, X, BookOpen, Clock, TrendingUp } from "lucide-react";
import LoadingSpinner from './loader';

const Prepify = () => {
  // Wrap dynamicPhrases in useMemo to prevent unnecessary re-renders
  const dynamicPhrases = useMemo(() => ["PREPARE", "GET STARTED", "PRACTICE", "SUCCEED", "SELF-TEST"], []);
  const [currentPhrase, setCurrentPhrase] = useState(dynamicPhrases[0]);
  const [isVisible, setIsVisible] = useState(true);
  const [showPopup, setShowPopup] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [imagesLoaded, setImagesLoaded] = useState(false);

  // Function to handle image loading
  const handleImageLoad = () => {
    setImagesLoaded(true);
  };

  useEffect(() => {
    // Create an array of all images to preload
    const imagesToPreload = ['/test1.png'];
    let loadedCount = 0;
    
    // Create image objects and track their loading
    const imageObjects = imagesToPreload.map(src => {
      const img = new Image();
      img.onload = () => {
        loadedCount++;
        if (loadedCount === imagesToPreload.length) {
          setImagesLoaded(true);
        }
      };
      img.onerror = () => {
        loadedCount++;
        if (loadedCount === imagesToPreload.length) {
          setImagesLoaded(true);
        }
      };
      img.src = src;
      return img;
    });

    // Setup font loading check
    document.fonts.ready.then(() => {
      // Check if all resources are loaded
      if (imagesLoaded) {
        setTimeout(() => {
          setIsLoading(false);
        }, 500); // Short delay to ensure smooth transition
      }
    });

    // Simulate minimum loading time (prevents flicker for fast connections)
    const minimumLoadingTimer = setTimeout(() => {
      checkAllLoaded();
    }, 2000);

    const checkAllLoaded = () => {
      if (imagesLoaded && document.fonts.status === 'loaded') {
        setTimeout(() => {
          setIsLoading(false);
        }, 500);
      }
    };

    // Set up the interval for phrase changing
    const interval = setInterval(() => {
      setIsVisible(false);
      setTimeout(() => {
        setCurrentPhrase((prevPhrase) => {
          const currentIndex = dynamicPhrases.indexOf(prevPhrase);
          const nextIndex = (currentIndex + 1) % dynamicPhrases.length;
          return dynamicPhrases[nextIndex];
        });
        setIsVisible(true);
      }, 500);
    }, 3000);
  
    // Cleanup function
    return () => {
      clearTimeout(minimumLoadingTimer);
      clearInterval(interval);
    };
  }, [dynamicPhrases, imagesLoaded]);
  
  // Effect to monitor image loading state and update loading state
  useEffect(() => {
    if (imagesLoaded && document.readyState === 'complete') {
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
  }, [imagesLoaded]);
  
  // In the return statement, conditionally render LoadingSpinner
  if (isLoading) {
    return <LoadingSpinner />;
  }

  const handlePopupClose = () => {
    setShowPopup(false);
  };

  const handleClaimOffer = () => {
    window.location.href = "https://app.prepify.live";
    setShowPopup(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-600 to-indigo-800 text-white px-4 sm:px-6 lg:px-8">
      {/* Popup Modal */}
      {showPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50">
          <div className="bg-white text-purple-900 p-8 rounded-xl w-full sm:w-96 shadow-2xl relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute -top-10 -left-10 w-32 h-32 bg-yellow-400 rounded-full opacity-20"></div>
            <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-purple-500 rounded-full opacity-20"></div>
            
            <button
              onClick={handlePopupClose}
              className="absolute top-2 right-2 text-purple-900 hover:text-gray-600 transition-colors"
            >
              <X size={24} />
            </button>
            <h2 className="text-3xl font-bold mb-2 text-center bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-indigo-600">Special Offer</h2>
            <div className="w-20 h-1 bg-yellow-400 mx-auto mb-4 rounded-full"></div>
            <p className="text-lg mb-4 text-center">
              <span className="font-bold text-indigo-600">FREE</span> until April 30th!
            </p>
            <p className="text-md mb-6 text-gray-700">
              Pre-register now for the trial version. Your subscription will become permanent after the software release.
            </p>
            <div className="flex justify-center mb-4">
              <CheckCircle size={56} className="text-yellow-400 drop-shadow-md" />
            </div>
            <div className="flex justify-center mt-4">
              <button
                onClick={handleClaimOffer}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white py-3 px-8 rounded-full font-semibold transition-transform transform hover:scale-105 shadow-lg"
              >
                Pre-register Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="fixed top-0 inset-x-0 flex justify-between items-center px-6 py-4 bg-gradient-to-r from-purple-800 to-indigo-800 backdrop-blur-lg shadow-lg z-50">
        <h1 className="text-3xl font-extrabold">
          <span className="text-yellow-400">Prep</span>ify
        </h1>
        <nav className="hidden md:flex space-x-6 text-lg font-medium">
          <a href="https://app.prepify.live" className="hover:text-yellow-400 transition-colors">App</a>
          <a href="#features" className="hover:text-yellow-400 transition-colors">Features</a>
          <a href="#pricing" className="hover:text-yellow-400 transition-colors">Pricing</a>
          <a href="https://docs.google.com/forms/d/e/1FAIpQLSelKEUaXdwW0c5Do99Z_4MEh66avJdh0sCz-I_BRqJ1WF3_yw/viewform?usp=header" className="hover:text-yellow-400 transition-colors">Contact</a>
        </nav>
        {/* Mobile menu */}
        <div className="md:hidden">
          <button onClick={toggleMobileMenu} className="text-white hover:text-yellow-400 transition-colors">
            ☰
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 bg-purple-900 bg-opacity-95 backdrop-blur-lg z-40 md:hidden">
          <div className="flex flex-col items-center justify-center h-full space-y-8">
            <button onClick={toggleMobileMenu} className="absolute top-4 right-4 text-white">
              <X size={32} />
            </button>
            <a href="https://app.prepify.live" className="text-white text-2xl hover:text-yellow-400 transition-colors">App</a>
            <a href="#features" className="text-white text-2xl hover:text-yellow-400 transition-colors" onClick={toggleMobileMenu}>Features</a>
            <a href="#pricing" className="text-white text-2xl hover:text-yellow-400 transition-colors" onClick={toggleMobileMenu}>Pricing</a>
            <a href="https://docs.google.com/forms/d/e/1FAIpQLSelKEUaXdwW0c5Do99Z_4MEh66avJdh0sCz-I_BRqJ1WF3_yw/viewform?usp=header" className="text-white text-2xl hover:text-yellow-400 transition-colors">Contact</a>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section id="about" className="flex flex-col md:flex-row items-center justify-center text-center md:text-left py-40 px-6">
        <div className="md:w-1/2 space-y-8">
          <div className="inline-block bg-purple-800 bg-opacity-30 px-4 py-1 rounded-full mb-2">
            <p className="text-yellow-400 font-medium">Ace Your Entrance Exams</p>
          </div>
          <h2 className="text-5xl sm:text-6xl lg:text-8xl font-bold" style={{ fontFamily: "Bebas Neue, sans-serif" }}>
            LET'S{" "}
            <span className="transition-opacity duration-1000 ease-in-out bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 to-yellow-300" style={{ opacity: isVisible ? 1 : 0 }}>
              {currentPhrase}
            </span>
          </h2>
          <p className="text-lg sm:text-xl leading-relaxed text-gray-100">
            Master entry tests effortlessly with Prepify. Get access to interactive practice tests,
            real-time scoring, personalized feedback, and in-depth analytics to track your progress.
          </p>
          <div className="mt-8">
            <button
              onClick={() => {
                const pricingSection = document.getElementById("pricing");
                if (pricingSection) {
                  pricingSection.scrollIntoView({ behavior: "smooth" });
                }
              }}
              className="bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-purple-900 py-4 px-10 rounded-full shadow-xl text-lg font-semibold transition-transform transform hover:scale-105"
            >
              Get Started
            </button>
          </div>
        </div>
        <div className="md:w-1/2 flex justify-center mt-12 md:mt-0">
          <div className="relative">
            <div className="absolute inset-0 bg-yellow-400 rounded-full opacity-20 blur-2xl transform -translate-x-4 translate-y-4"></div>
            <img 
              src="/test1.png" 
              alt="Prepify branding" 
              className="relative z-10 w-full max-w-xs sm:max-w-sm lg:max-w-lg h-auto rounded-lg shadow-2xl" 
              onLoad={handleImageLoad}
            />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="px-6 py-20 bg-gradient-to-b from-indigo-900 to-purple-900 text-white w-full mt-20 rounded-t-3xl">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block bg-indigo-800 bg-opacity-50 px-4 py-1 rounded-full mb-3">
              <p className="text-yellow-400 font-medium">Why Choose Us</p>
            </span>
            <h2 className="text-4xl font-bold mb-4">Powerful Features</h2>
            <div className="w-20 h-1 bg-yellow-400 mx-auto"></div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { 
                title: "Interactive Tests", 
                desc: "Engage with real-time, interactive tests designed for optimal learning.", 
                icon: <Edit3 size={44} className="text-yellow-400" /> 
              },
              { 
                title: "Personalized Feedback", 
                desc: "Receive instant, detailed feedback to improve your weak areas.", 
                icon: <CheckCircle size={44} className="text-yellow-400" /> 
              },
              { 
                title: "Comprehensive Analytics", 
                desc: "Track your progress with detailed performance analytics.", 
                icon: <BarChart3 size={44} className="text-yellow-400" /> 
              },
              { 
                title: "5000+ MCQs", 
                desc: "Access a vast library of over 5000+ MCQs covering various subjects and topics.", 
                icon: <BookOpen size={44} className="text-yellow-400" /> 
              },
              { 
                title: "Self-Testing Opportunity", 
                desc: "Practice at your own pace with self-testing modules and timed mock exams.", 
                icon: <Clock size={44} className="text-yellow-400" /> 
              },
              { 
                title: "Real-Time Scoring", 
                desc: "Get instant scores and performance reports after each test attempt.", 
                icon: <TrendingUp size={44} className="text-yellow-400" /> 
              }
            ].map((feature, index) => (
              <div key={index} className="bg-gradient-to-br from-purple-800 to-indigo-900 p-8 rounded-2xl shadow-xl transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 group">
                <div className="bg-indigo-900 bg-opacity-50 w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:bg-yellow-400 group-hover:bg-opacity-20 transition-colors duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold mb-3 group-hover:text-yellow-400 transition-colors">{feature.title}</h3>
                <p className="text-gray-300">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Subscription Pricing Section */}
      <section id="pricing" className="text-center py-20 bg-gradient-to-b from-purple-900 to-indigo-900 w-full">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block bg-purple-800 bg-opacity-50 px-4 py-1 rounded-full mb-3">
              <p className="text-yellow-400 font-medium">Pricing Plans</p>
            </span>
            <h2 className="text-4xl font-bold mb-4">Choose Your Plan</h2>
            <div className="w-20 h-1 bg-yellow-400 mx-auto mb-6"></div>
            <p className="text-lg text-gray-300">Over 5000+ MCQs available!</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 px-6">
            {[
              { 
                plan: "NUST Test", 
                status: "Launching Soon", 
                price: "1999 PKR/yearly", 
                features: ["Engineering Test", "Business Test", "Social Science Test"],
                highlighted: true
              },
              { 
                plan: "ECAT", 
                status: "Coming Soon", 
                price: "Stay Tuned!", 
                features: ["Engineering College Admission Test", "Complete Study Material", "Full Practice Tests"]
              },
              { 
                plan: "SAT", 
                status: "Coming Soon", 
                price: "Stay Tuned!", 
                features: ["Scholastic Assessment Test", "Verbal & Math Sections", "Essay Practice"]
              }
            ].map((tier, index) => (
              <div key={index} className={`${tier.highlighted ? "bg-gradient-to-br from-purple-600 to-indigo-800 scale-105 border-2 border-yellow-400" : "bg-white text-purple-900"} p-8 rounded-2xl shadow-xl transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 relative overflow-hidden`}>
                {tier.highlighted && (
                  <div className="absolute top-0 right-0">
                    <div className="bg-yellow-400 text-purple-900 font-bold py-1 px-4 rounded-bl-lg text-xs shadow-md">
                      POPULAR
                    </div>
                  </div>
                )}
                <h3 className={`text-2xl font-bold ${tier.highlighted ? "text-white" : "text-purple-900"}`}>{tier.plan}</h3>
                <p className={`text-sm font-medium mt-1 mb-3 ${tier.highlighted ? "text-yellow-400" : "text-indigo-600"}`}>{tier.status}</p>
                <p className={`text-xl font-semibold my-4 ${tier.highlighted ? "text-white" : "text-purple-900"}`}>{tier.price}</p>
                <ul className={`text-sm space-y-3 mb-6 ${tier.highlighted ? "text-gray-300" : "text-purple-700"}`}>
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center">
                      <CheckCircle size={16} className={`mr-2 ${tier.highlighted ? "text-yellow-400" : "text-indigo-600"}`} />
                      {feature}
                    </li>
                  ))}
                </ul>
                <button 
                  onClick={handleClaimOffer} 
                  className={`mt-4 ${
                    tier.highlighted 
                      ? "bg-yellow-400 hover:bg-yellow-500 text-purple-900" 
                      : "bg-purple-700 hover:bg-purple-800 text-white"
                  } py-3 px-6 rounded-lg font-medium w-full transition-colors`}
                >
                  Pre-register
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-purple-900 to-indigo-900 py-10 text-center text-white mt-16 w-full">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-6 md:mb-0">
              <h2 className="text-2xl font-bold mb-2">
                <span className="text-yellow-400">Prep</span>ify
              </h2>
              <p className="text-gray-300">Prepare, Practice, Succeed</p>
            </div>
            
            <div className="flex space-x-6 mb-6 md:mb-0">
              <a href="#about" className="text-gray-300 hover:text-white transition-colors">Home</a>
              <a href="#features" className="text-gray-300 hover:text-white transition-colors">Features</a>
              <a href="#pricing" className="text-gray-300 hover:text-white transition-colors">Pricing</a>
              <a href="https://docs.google.com/forms/d/e/1FAIpQLSelKEUaXdwW0c5Do99Z_4MEh66avJdh0sCz-I_BRqJ1WF3_yw/viewform?usp=header" className="text-gray-300 hover:text-white transition-colors">Contact</a>
            </div>
            
            <div>
              <a href="https://www.instagram.com/prepify.live/" className="text-white hover:text-yellow-400 transition-colors">
                <Instagram size={28} />
              </a>
            </div>
          </div>
          
          <div className="mt-8 pt-6 border-t border-gray-700">
            <p className="text-gray-400">&copy; 2025 Prepify. All Rights Reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Prepify;